import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Edit05, Trash01 } from "@untitledui/icons";
import { useAuth } from "@/context/AuthProvider";
import PageLayout from "@/components/layout/PageLayout";
import { Button } from "@/components/base/buttons/button";
import { Dialog, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import VehicleForm from "./VehicleForm";
import {
  getVehicle,
  updateVehicle,
  deleteVehicle,
  type VehicleWithCustomer,
  type CreateVehiclePayload,
} from "@/api/vehicles";
import { getCustomers } from "@/api/customers";
import { getJobs, type JobWithRelations } from "@/api/jobs";
import type { CustomerDto } from "@/types/dto";

const CAN_EDIT = ["admin", "owner", "manager"];
const CAN_DELETE = ["admin", "owner"];

export default function VehicleDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [vehicle, setVehicle] = useState<VehicleWithCustomer | null>(null);
  const [customers, setCustomers] = useState<CustomerDto[]>([]);
  const [jobs, setJobs] = useState<JobWithRelations[]>([]);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const canEdit = CAN_EDIT.includes(user?.role ?? "");
  const canDelete = CAN_DELETE.includes(user?.role ?? "");

  const fetchVehicle = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const data = await getVehicle(id);
      setVehicle(data);
    } catch {
      navigate("/vehicles", { replace: true });
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  const fetchCustomers = useCallback(async () => {
    try {
      const data = await getCustomers({ limit: 100 });
      setCustomers(data.data);
    } catch {
      // handled
    }
  }, []);

  const fetchJobs = useCallback(async () => {
    if (!id) return;
    try {
      const data = await getJobs({ vehicleId: id, limit: 100 });
      setJobs(data.data);
    } catch {
      // handled
    }
  }, [id]);

  useEffect(() => {
    fetchVehicle();
    fetchCustomers();
    fetchJobs();
  }, [fetchVehicle, fetchCustomers, fetchJobs]);

  const handleEdit = async (payload: CreateVehiclePayload) => {
    if (!id) return;
    setSaving(true);
    try {
      await updateVehicle(id, payload);
      setEditOpen(false);
      fetchVehicle();
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!id) return;
    setSaving(true);
    try {
      await deleteVehicle(id);
      navigate("/vehicles", { replace: true });
    } finally {
      setSaving(false);
    }
  };

  if (loading || !vehicle) {
    return (
      <PageLayout title="Loading...">
        <div className="py-12 text-center text-text-tertiary">Loading vehicle...</div>
      </PageLayout>
    );
  }

  return (
    <PageLayout
      title={`${vehicle.make} ${vehicle.model}`}
      subtitle={`${vehicle.year} — ${vehicle.licensePlate}`}
      actions={
        <div className="flex items-center gap-2">
          {canEdit && (
            <Button
              size="md"
              color="secondary"
              iconLeading={Edit05}
              onClick={() => setEditOpen(true)}
            >
              Edit
            </Button>
          )}
          {canDelete && (
            <Button
              size="md"
              color="primary-destructive"
              iconLeading={Trash01}
              onClick={() => setDeleteOpen(true)}
            >
              Delete
            </Button>
          )}
        </div>
      }
    >
      <div className="mb-6">
        <Button
          size="sm"
          color="link-gray"
          iconLeading={ArrowLeft}
          onClick={() => navigate("/vehicles")}
        >
          Back to Vehicles
        </Button>
      </div>

      {/* Vehicle Info */}
      <div className="rounded-xl border border-border-secondary bg-bg-primary p-4 sm:p-6 mb-6 sm:mb-8">
        <h2 className="text-md font-semibold text-text-primary mb-4">Vehicle Information</h2>
        <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4 text-sm">
          <div>
            <dt className="text-text-tertiary">Make & Model</dt>
            <dd className="text-text-primary font-medium mt-0.5">
              {vehicle.make} {vehicle.model}
            </dd>
          </div>
          <div>
            <dt className="text-text-tertiary">Year</dt>
            <dd className="text-text-primary mt-0.5">{vehicle.year}</dd>
          </div>
          <div>
            <dt className="text-text-tertiary">Color</dt>
            <dd className="text-text-primary mt-0.5">{vehicle.color ?? "—"}</dd>
          </div>
          <div>
            <dt className="text-text-tertiary">Reg No (License Plate)</dt>
            <dd className="text-text-primary mt-0.5">{vehicle.licensePlate}</dd>
          </div>
          <div>
            <dt className="text-text-tertiary">Date of Registration</dt>
            <dd className="text-text-primary mt-0.5">{vehicle.registrationDate ?? "—"}</dd>
          </div>
          <div>
            <dt className="text-text-tertiary">Chassis No (VIN)</dt>
            <dd className="text-text-primary mt-0.5">{vehicle.vin ?? "—"}</dd>
          </div>
          <div>
            <dt className="text-text-tertiary">Engine No</dt>
            <dd className="text-text-primary mt-0.5">{vehicle.engineNumber ?? "—"}</dd>
          </div>
          <div>
            <dt className="text-text-tertiary">Mileage (Km)</dt>
            <dd className="text-text-primary mt-0.5">
              {vehicle.mileage != null ? vehicle.mileage.toLocaleString() : "—"}
            </dd>
          </div>
          <div>
            <dt className="text-text-tertiary">Owner</dt>
            <dd className="text-text-primary mt-0.5">
              {vehicle.customer ? (
                <Link
                  to={`/customers/${vehicle.customer.id}`}
                  className="text-fg-brand-primary hover:underline"
                >
                  {vehicle.customer.firstName} {vehicle.customer.lastName}
                </Link>
              ) : (
                <span className="text-text-tertiary">No owner</span>
              )}
            </dd>
          </div>
          {vehicle.notes && (
            <div className="md:col-span-2">
              <dt className="text-text-tertiary">Notes</dt>
              <dd className="text-text-primary mt-0.5">{vehicle.notes}</dd>
            </div>
          )}
        </dl>
      </div>

      {/* Jobs */}
      <div className="rounded-xl border border-border-secondary bg-bg-primary mt-6 sm:mt-8">
        <div className="px-4 sm:px-6 py-4 border-b border-border-secondary">
          <h2 className="text-md font-semibold text-text-primary">
            Jobs ({jobs.length})
          </h2>
        </div>
        {jobs.length > 0 ? (
          <>
            {/* Mobile cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 p-4 xl:hidden">
              {jobs.map((j) => (
                <div key={j.id} className="rounded-lg border border-border-secondary p-3">
                  <div className="flex items-center justify-between">
                    <p className="font-medium text-text-primary">Job #{j.jobNumber}</p>
                    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium capitalize ${
                      j.status === "completed" || j.status === "invoiced" ? "bg-green-100 text-green-700"
                      : j.status === "in_progress" ? "bg-blue-100 text-blue-700"
                      : j.status === "awaiting_parts" ? "bg-yellow-100 text-yellow-700"
                      : j.status === "cancelled" ? "bg-red-100 text-red-700"
                      : "bg-gray-100 text-gray-700"
                    }`}>
                      {j.status.replace(/_/g, " ")}
                    </span>
                  </div>
                  {j.description && (
                    <p className="text-sm text-text-secondary mt-1 line-clamp-2">{j.description}</p>
                  )}
                  {j.assignee && (
                    <p className="text-xs text-text-tertiary mt-1">
                      Assigned to: {j.assignee.firstName} {j.assignee.lastName}
                    </p>
                  )}
                </div>
              ))}
            </div>

            {/* Desktop table */}
            <table className="hidden xl:table w-full text-sm">
              <thead>
                <tr className="border-b border-border-secondary bg-bg-secondary">
                  <th className="px-6 py-3 text-left font-medium text-text-tertiary">Job #</th>
                  <th className="px-6 py-3 text-left font-medium text-text-tertiary">Description</th>
                  <th className="px-6 py-3 text-left font-medium text-text-tertiary">Status</th>
                  <th className="px-6 py-3 text-left font-medium text-text-tertiary">Assigned To</th>
                </tr>
              </thead>
              <tbody>
                {jobs.map((j) => (
                  <tr key={j.id} className="border-b border-border-secondary last:border-0">
                    <td className="px-6 py-4 font-medium text-text-primary">#{j.jobNumber}</td>
                    <td className="px-6 py-4 text-text-secondary max-w-xs truncate">{j.description ?? "—"}</td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium capitalize ${
                        j.status === "completed" || j.status === "invoiced" ? "bg-green-100 text-green-700"
                        : j.status === "in_progress" ? "bg-blue-100 text-blue-700"
                        : j.status === "awaiting_parts" ? "bg-yellow-100 text-yellow-700"
                        : j.status === "cancelled" ? "bg-red-100 text-red-700"
                        : "bg-gray-100 text-gray-700"
                      }`}>
                        {j.status.replace(/_/g, " ")}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-text-secondary">
                      {j.assignee ? `${j.assignee.firstName} ${j.assignee.lastName}` : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        ) : (
          <div className="px-6 py-8 text-center text-text-tertiary">
            No jobs for this vehicle.
          </div>
        )}
      </div>

      {/* Edit Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogTitle>Edit Vehicle</DialogTitle>
        <DialogDescription>Update the vehicle's information below.</DialogDescription>
        <VehicleForm
          key={vehicle.id}
          customers={customers}
          defaultValues={vehicle}
          onSubmit={handleEdit}
          onCancel={() => setEditOpen(false)}
          isLoading={saving}
        />
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogTitle>Delete Vehicle</DialogTitle>
        <DialogDescription>
          Are you sure you want to delete this {vehicle.make} {vehicle.model}? This action cannot
          be undone.
        </DialogDescription>
        <div className="flex justify-end gap-3">
          <Button size="md" color="secondary" onClick={() => setDeleteOpen(false)}>
            Cancel
          </Button>
          <Button
            size="md"
            color="primary-destructive"
            isLoading={saving}
            isDisabled={saving}
            onClick={handleDelete}
          >
            Delete
          </Button>
        </div>
      </Dialog>
    </PageLayout>
  );
}
